import type { RatingUpdatePayload } from '@bunker/shared';
import i18n from '@/i18n';
import { areNotificationsEnabled } from '@/lib/notifications';
import { toast } from '@/lib/toast';

type Translate = (key: string, vars?: Record<string, unknown>) => string;

const signed = (v: number) => `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(v)}`;

/** ratingUpdateText says in one line what the new rating did for the player. */
export function ratingUpdateText(t: Translate, p: RatingUpdatePayload): { title: string; body: string } {
  const title = t('reconRating.title');
  if (p.new) return { title, body: t('reconRating.new', { rank: p.rank, cr: p.cr }) };
  if (p.rankDelta > 0) {
    return { title, body: t('reconRating.up', { count: p.rankDelta, rank: p.rank, cr: p.cr, delta: signed(p.crDelta) }) };
  }
  if (p.rankDelta < 0) {
    return { title, body: t('reconRating.down', { count: -p.rankDelta, rank: p.rank, cr: p.cr, delta: signed(p.crDelta) }) };
  }
  return { title, body: t('reconRating.same', { rank: p.rank, cr: p.cr, delta: signed(p.crDelta) }) };
}

/** openCard opens the player's card; the desktop shell routes it to Recon. */
function openCard(url: string) {
  window.open(url, '_blank', 'noopener');
}

/** showRatingUpdate surfaces a published rating to its player: an in-app toast
 *  with a way into the card, and an OS banner when the user turned those on and
 *  the app is in the background. */
export function showRatingUpdate(p: RatingUpdatePayload): void {
  const t: Translate = (key, vars) => i18n.t(key, vars);
  const { title, body } = ratingUpdateText(t, p);
  toast.info(title, {
    description: body,
    duration: 15000,
    action: { label: t('reconRating.open'), onClick: () => openCard(p.url) },
  });
  if (!areNotificationsEnabled() || document.visibilityState === 'visible') return;
  const n = new window.Notification(title, { body, tag: 'recon-rating' });
  n.onclick = () => {
    window.focus();
    openCard(p.url);
    n.close();
  };
}
